import { z } from "zod";

import { COUNTRY_LABELS } from "./countries";
import { CUSTOMER_STAGE_LABELS, customerDetail, missingCustomerFields, type Customer } from "./customers";
import { missingOtherPartyFields, otherPartyDetail, type OtherParty } from "./other-parties";
import { calendarDateSchema, PERSON_TYPE_LABELS, utcToday, type Person } from "./people";
import { missingVendorFields, vendorDetail, type Vendor } from "./vendors";

// The company directory: every party the business has a relationship with, whatever kind of record it is kept as.
// Each kind lives in a table of its own; a projector function turns each change into one row of the directory table
// (`apps/backend/lib/api/directory/projector.ts`). Nothing writes the directory table through the API.

/** What kind of record a party is kept as. Each kind has a table, a form and a page of its own. */
export const PARTY_KINDS = ["person", "vendor", "customer", "other"] as const;
export type PartyKind = (typeof PARTY_KINDS)[number];

/**
 * The kinds the Directory owns outright — kept in its own tables, edited on its own pages — as opposed to a person, whom
 * People & HR owns. They are also the kinds the Directory agent adds, and the kinds a work item can ask to complete.
 */
export const DIRECTORY_OWNED_KINDS = ["vendor", "customer", "other"] as const satisfies readonly PartyKind[];
export type DirectoryOwnedKind = (typeof DIRECTORY_OWNED_KINDS)[number];

/** What a sentence calls a party of each kind the Directory owns: "Complete this vendor", "Save party". */
export const OWNED_PARTY_NOUNS: Readonly<Record<DirectoryOwnedKind, string>> = {
	vendor: "vendor",
	customer: "customer",
	other: "party",
};

export const PARTY_KIND_LABELS: Readonly<Record<PartyKind, string>> = {
	person: "Person",
	vendor: "Vendor",
	customer: "Customer",
	other: "Other",
};

/** The sort-key prefix each kind's own table keeps its records under; the projector reads a kind back from it. */
export const PARTY_SORT_PREFIX: Readonly<Record<PartyKind, string>> = {
	person: "PERSON#",
	vendor: "VENDOR#",
	customer: "CUSTOMER#",
	other: "OTHER#",
};

/**
 * Where a party stands today, in the one vocabulary every kind maps onto so the list can tone them together. A party
 * whose record still lacks a field a person must fill in is `incomplete`, whatever its dates say — it may have none.
 */
export const PARTY_STANDINGS = ["upcoming", "active", "past", "incomplete"] as const;
export type PartyStanding = (typeof PARTY_STANDINGS)[number];

/** What the list calls a party whose relationship has not started, and one whose relationship is over. */
export const PARTY_UPCOMING_LABEL = "Starting";
export const PARTY_PAST_LABEL = "Ended";
/** What the list calls a party the Directory agent added and nobody has finished yet. */
export const PARTY_INCOMPLETE_LABEL = "Incomplete";

export const directoryEntrySchema = z.object({
	kind: z.enum(PARTY_KINDS),
	/** The party's id in the table that owns it, which is also what its row links to. */
	id: z.string().min(1).max(64),
	/** What the party is called, which is also what the list is ordered and searched by. */
	name: z.string().min(1).max(160),
	/** The line under the name: a person's role and team, a vendor's category, a customer's segment, a label. */
	detail: z.string().min(1).max(140),
	/** Whom to write to, when the record names anybody. */
	email: z.email().optional(),
	country: z.string().min(1).max(100).optional(),
	/**
	 * The name a vendor or a customer is registered under, when its record names one. The list never shows it; it is
	 * projected so that whatever reads the Directory to tell one party from another — the Directory agent and the
	 * Directory's knowledge base — finds a party by it without reading the table that owns the record.
	 */
	legalName: z.string().min(1).max(160).optional(),
	/** A vendor's or a customer's tax id as its record keeps it, projected for the same reason as `legalName`. */
	taxId: z.string().min(1).max(40).optional(),
	/**
	 * The day the relationship started: a person's first day, another party's "since". Absent only while an incomplete
	 * party still lacks it.
	 */
	since: calendarDateSchema.optional(),
	/** The last day of the relationship, inclusive; absent while nobody has said it ends. */
	until: calendarDateSchema.optional(),
	/**
	 * What this kind calls the party while the relationship is live, which is the word the status pill shows:
	 * "Employee" or "Contractor" for a person, "Active" for a vendor and for an "other" party, and a customer's own
	 * stage — "Prospect" or "Customer". Those five are every word the four kinds project.
	 */
	activeLabel: z.string().min(1).max(40),
	/**
	 * Present, and true, while the record lacks a field a person must fill in (`missingVendorFields` and the like): the
	 * Directory agent added it with what a document said. It is projected from the record like everything else here,
	 * so it goes the moment somebody saves the record whole.
	 */
	incomplete: z.literal(true).optional(),
	/** When the projector last wrote this row, as an ISO 8601 timestamp in UTC. */
	projectedAt: z.iso.datetime(),
});
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;

/**
 * Where a party stands on `today` and what the list calls that: "Incomplete" while its record lacks a field a person
 * must fill in, then "Ended" once the last day has passed, "Starting" until the first day has come, and the kind's own
 * word on every day in between. Both dates count as days the relationship is on. Never stored, so it can never go
 * stale — the same rule `personStatus` follows.
 */
export function partyStatus(
	party: Pick<DirectoryEntry, "since" | "until" | "activeLabel" | "incomplete">,
	today: string = utcToday(),
): { standing: PartyStanding; label: string } {
	if (party.incomplete === true || party.since === undefined) {
		return { standing: "incomplete", label: PARTY_INCOMPLETE_LABEL };
	}
	if (party.until !== undefined && party.until < today) {
		return { standing: "past", label: PARTY_PAST_LABEL };
	}
	if (party.since > today) {
		return { standing: "upcoming", label: PARTY_UPCOMING_LABEL };
	}
	return { standing: "active", label: party.activeLabel };
}

/** A person as the Directory lists them: their role and team under their name, and their dates as they stand. */
export function directoryEntryForPerson(person: Person, projectedAt: string): DirectoryEntry {
	return {
		kind: "person",
		id: person.id,
		name: person.name,
		detail: `${person.role} · ${person.team}`,
		email: person.email,
		...(person.country !== undefined && { country: COUNTRY_LABELS[person.country] }),
		since: person.startDate,
		...(person.endDate !== undefined && { until: person.endDate }),
		activeLabel: PERSON_TYPE_LABELS[person.type],
		projectedAt,
	};
}

/** An "other" party as the Directory lists them: their label under their name. */
export function directoryEntryForOtherParty(party: OtherParty, projectedAt: string): DirectoryEntry {
	return {
		kind: "other",
		id: party.id,
		name: party.name,
		detail: otherPartyDetail(party),
		...(party.email !== undefined && { email: party.email }),
		...(party.country !== undefined && party.country !== "" && { country: party.country }),
		...(party.since !== undefined && { since: party.since }),
		...(party.endDate !== undefined && { until: party.endDate }),
		activeLabel: "Active",
		...(missingOtherPartyFields(party).length > 0 && { incomplete: true as const }),
		projectedAt,
	};
}

/** A vendor as the Directory lists them: what they supply under their name, and the names and tax id it is known by. */
export function directoryEntryForVendor(vendor: Vendor, projectedAt: string): DirectoryEntry {
	return {
		kind: "vendor",
		id: vendor.id,
		name: vendor.name,
		detail: vendorDetail(vendor),
		...(vendor.email !== undefined && { email: vendor.email }),
		...(vendor.country !== undefined && vendor.country !== "" && { country: vendor.country }),
		...(vendor.legalName !== undefined && vendor.legalName !== "" && { legalName: vendor.legalName }),
		...(vendor.taxId !== undefined && vendor.taxId !== "" && { taxId: vendor.taxId }),
		...(vendor.since !== undefined && { since: vendor.since }),
		...(vendor.endDate !== undefined && { until: vendor.endDate }),
		activeLabel: "Active",
		...(missingVendorFields(vendor).length > 0 && { incomplete: true as const }),
		projectedAt,
	};
}

/**
 * A customer as the Directory lists them: their segment under their name, the names and tax id they are known by, and
 * their stage as the word the status pill shows while the relationship is on — so a live prospect reads "Prospect", a live customer reads "Customer", and one
 * given an end date that has passed reads "Ended", because `partyStatus` weighs the dates before the stage. One whose
 * stage nobody has chosen yet is incomplete, which the pill shows instead, so its `activeLabel` is never read.
 */
export function directoryEntryForCustomer(customer: Customer, projectedAt: string): DirectoryEntry {
	return {
		kind: "customer",
		id: customer.id,
		name: customer.name,
		detail: customerDetail(customer),
		...(customer.email !== undefined && { email: customer.email }),
		...(customer.country !== undefined && customer.country !== "" && { country: customer.country }),
		...(customer.legalName !== undefined && customer.legalName !== "" && { legalName: customer.legalName }),
		...(customer.taxId !== undefined && customer.taxId !== "" && { taxId: customer.taxId }),
		...(customer.since !== undefined && { since: customer.since }),
		...(customer.endDate !== undefined && { until: customer.endDate }),
		activeLabel: customer.stage === undefined ? "Active" : CUSTOMER_STAGE_LABELS[customer.stage],
		...(missingCustomerFields(customer).length > 0 && { incomplete: true as const }),
		projectedAt,
	};
}

/** The directory comes a page of this many parties at a time, A→Z by name. */
export const DIRECTORY_PAGE_SIZE = 10;

/** A link to another page of the Directory. The API makes it; a client passes it back unchanged and never reads it. */
export const directoryPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/u, "Invalid page");

/** The longest search the API takes, which is also as much as the search box lets anyone type. */
export const DIRECTORY_QUERY_MAX_LENGTH = 80;

/** The query of `GET /api/directory`: which kinds to show, what to search for, and which page. */
export const directoryQuerySchema = z.object({
	kind: z.enum(PARTY_KINDS).optional(),
	/** Matched against a party's name, without regard to case; every party when it is absent or empty. */
	q: z.string().trim().max(DIRECTORY_QUERY_MAX_LENGTH).optional(),
	page: directoryPageTokenSchema.optional(),
});
export type DirectoryQuery = z.infer<typeof directoryQuerySchema>;

/** One page of the directory, A→Z by name, with links to the pages beside it: `null` where there is none. */
export const directoryPageSchema = z.object({
	parties: z.array(directoryEntrySchema).max(DIRECTORY_PAGE_SIZE),
	/** Every party of the organization counted by kind, whichever page and whichever filter this is. */
	counts: z.record(z.enum(PARTY_KINDS), z.int().nonnegative()),
	/** How many parties the filter matches, which is what the card's heading says. */
	total: z.int().nonnegative(),
	next: directoryPageTokenSchema.nullable(),
	previous: directoryPageTokenSchema.nullable(),
});
export type DirectoryPage = z.infer<typeof directoryPageSchema>;

/**
 * Parties A→Z by name; two with the same name order by id, so every read lists them the same way. Name and id are
 * exactly what a page link carries, so a page can count how many parties come before the one it names and be right —
 * ordering by anything else between the two would let a page skip a party or show it twice.
 */
export function byPartyName(parties: readonly DirectoryEntry[]): DirectoryEntry[] {
	return parties.toSorted((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}
